import { useContext, useRef, useState } from 'react';
import {
  CalendarIcon,
  CalendarPlus,
  LocationIcon,
  MarkerPinIcon,
} from 'assets/icons';
import { DateMenu } from 'common/date-picker/components/date-menu';
import { LocationMenu } from 'common/album-detail-modal/components/menus/location-menu';
import { NotificationType } from 'common/enums';
import { appActions, AppContext } from 'context/app-context';
import { classnames } from 'helpers/utils';
import { memoryDateLabel } from 'helpers/memory-date-label';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { CenterModal } from '../center-modal';
import styles from './details-panel.module.scss';

interface MemoryTagsProps {
  content: AlbumContentSerialized | undefined;
  onContentUpdated: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  // 'panel': stacked in the details column, the locations in a dropdown.
  // 'page': the phone screen's row under the title, as in the mobile app;
  // the locations open in a dialog, which a phone has room for.
  variant?: 'panel' | 'page';
  // The public view: only what's set, as plain text.
  readOnly?: boolean;
}

// The memory's own location and date, each a button that changes it.
export const MemoryTags = ({
  content,
  onContentUpdated,
  variant = 'panel',
  readOnly = false,
}: MemoryTagsProps) => {
  const { dispatch } = useContext(AppContext);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);
  const page = variant === 'page';
  useClickOutside(
    locationRef,
    () => {
      if (!page) setShowLocationMenu(false);
    },
    { ignoreSelector: '[data-modal="location"]' },
  );

  const notify = (title: string, type: NotificationType) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({ show: true, title, type }),
    });

  const handleUpdateLocation = async (newLocation: string) => {
    if (!content) return;
    try {
      await VaultsController.updateContentMetadata(content.id, {
        location: newLocation,
      });
      onContentUpdated(content.id, { location: newLocation });
      setShowLocationMenu(false);
      notify('Location updated', NotificationType.Success);
    } catch (error) {
      notify('The location could not be updated', NotificationType.Error);
    }
  };

  const handleUpdateDate = async (startDate: string, endDate: string) => {
    if (!content) return;
    try {
      await VaultsController.updateContentMetadata(content.id, {
        startDate,
        endDate,
      });
      onContentUpdated(content.id, { startDate, endDate });
      notify('Date updated', NotificationType.Success);
    } catch (error) {
      notify('The date could not be updated', NotificationType.Error);
    }
  };

  const dateLabel = memoryDateLabel(content?.startDate, content?.endDate);
  const PinIcon = page ? MarkerPinIcon : LocationIcon;

  if (readOnly) {
    if (!content?.location && !dateLabel) return null;
    return (
      <div className={classnames(styles.tags, { [styles.tagsPage]: page })}>
        {content?.location && (
          <span className={styles.tagStatic}>
            <PinIcon className={styles.tagIcon} />
            <span className={styles.tagText}>{content.location}</span>
          </span>
        )}
        {dateLabel && (
          <span className={styles.tagStatic}>
            <CalendarIcon className={styles.tagIcon} />
            <span className={styles.tagText}>{dateLabel}</span>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={classnames(styles.tags, { [styles.tagsPage]: page })}>
      <div className={styles.tagWrap} ref={locationRef}>
        <button
          type="button"
          className={styles.tag}
          onClick={() => setShowLocationMenu((prev) => !prev)}
        >
          <PinIcon className={styles.tagIcon} />
          <span
            className={classnames(styles.tagText, {
              [styles.tagPlaceholder]: !content?.location,
            })}
          >
            {content?.location || 'Add location'}
          </span>
        </button>
        {showLocationMenu && !page && (
          <LocationMenu addLocation={handleUpdateLocation} />
        )}
      </div>

      <button
        type="button"
        className={styles.tag}
        onClick={() => setShowDateModal(true)}
      >
        {dateLabel ? (
          <CalendarIcon className={styles.tagIcon} />
        ) : (
          <CalendarPlus className={styles.tagIcon} />
        )}
        <span
          className={classnames(styles.tagText, {
            [styles.tagPlaceholder]: !dateLabel,
          })}
        >
          {dateLabel || 'Add date'}
        </span>
      </button>

      {showLocationMenu && page && (
        <CenterModal
          onClose={() => setShowLocationMenu(false)}
          className={styles.locationCard}
        >
          <div className={styles.modalBody}>
            <span className={styles.modalTitle}>Location</span>
            <LocationMenu renderInline addLocation={handleUpdateLocation} />
          </div>
        </CenterModal>
      )}

      {showDateModal && (
        <CenterModal onClose={() => setShowDateModal(false)}>
          <DateMenu
            renderInline
            subject="memory"
            currentDate={content?.startDate}
            currentFinalDate={content?.endDate}
            onUpdateDate={(start, end) => {
              handleUpdateDate(start, end);
              setShowDateModal(false);
            }}
          />
        </CenterModal>
      )}
    </div>
  );
};
